import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  blobToDataUrl,
  uploadBlob,
} from '@/modules/common/components/inputs/functions/commonUploadFileFunctions';
import FactorOutstandingInvoice from '@/app/spraypaint/factor/FactorOutstandingInvoice';
import { useBackendTable } from '@/modules/common/components/table/hooks';
import { Blob, ModelRecord } from '@/app/spraypaint';
import { toast } from 'react-toastify';
import { Toaster } from '@/modules/common/components/toasts';
import { UploadDocInput } from '@/modules/common/components/inputs';
import Table from '@/modules/common/components/table/Table';
import { onRowClickNavigate } from '@/app/navigation/functions';
import { useNavigate } from 'react-router-dom';
import { outstandingInvoiceColumns } from '../columns/outstandingInvoiceColumns';
import AuthorizationsFileCard from '../../authorizations/components/AuthorizationFileCard';
import { useGetLatestAuthorizationFile } from '../../authorizations/hooks/useGetLatestAuthorizationFile';
import { useGetOutstandingInvoices } from '../hooks/useGetOutstandingInvoices';
import { createAuthorizationFile } from '../../authorizations/hooks/useCreateAuthorizationFile';

const OutstandingInvoicesIndex = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filename, setFilename] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

  const { mutate: mutateBlob, isLoading: blobLoader } = useMutation(uploadBlob);
  const { mutate: mutateAuthorizationFile } = useMutation(
    createAuthorizationFile,
  );

  const [scope, scopeKey, props] = useBackendTable<FactorOutstandingInvoice>({
    initialSorting: [{ id: 'registered_client.name', desc: true }],
  });
  const { outstandingInvoices, isLoading, totalCount } =
    useGetOutstandingInvoices(scopeKey, scope);
  const { latestAuthorizationFile } = useGetLatestAuthorizationFile();

  const handleUploadFile = async () => {
    if (!selectedFile) return;
    const bytes = await blobToDataUrl(selectedFile);

    const payload = {
      filename,
      contentType: 'sepa-file',
      content: { bytes },
    };

    mutateBlob(payload, {
      onSuccess: (data: Blob) => {
        const blobPayload = {
          fileSignedId: data.id,
        };
        mutateAuthorizationFile(blobPayload, {
          onSuccess: () => {
            queryClient.invalidateQueries(['factor-authorizations']);
            queryClient.invalidateQueries(['factor-latest_authorization_file']);
            toast.success(
              <Toaster
                textMsg={t(
                  'front.factor.authorization.index.toats.upload_file.success',
                )}
                toastType="success"
              />,
            );
          },
          onError: () => {
            toast.error(
              <Toaster
                textMsg={t(
                  'front.factor.authorization.index.toats.upload_file.error',
                )}
                toastType="error"
              />,
            );
          },
        });
        setFilename('');
      },
      onError: () => {
        const errorText = `ERROR during import of ${selectedFile.name}`;
        toast.error(<Toaster textMsg={errorText} toastType="error" />);
      },
    });
  };

  const handleChangeFile = (file: File) => {
    setSelectedFile(file);
  };

  const handleOnRowClick =
    (raw: ModelRecord<FactorOutstandingInvoice>) => (event: MouseEvent) => {
      onRowClickNavigate({
        id: raw.adminShowPagePath,
        event,
        windowNavigationHref: raw.adminShowPagePath,
        navigate,
      });
    };

  return (
    <div className="mb-[100px]">
      <div className="ml-[20px]">
        <div className="flex justify-between pr-[20px] mb-[20px]">
          <p className="big-title ">
            {t('front.factor.authorization.index.title')}
          </p>
          <UploadDocInput
            isLoading={blobLoader}
            handleChangeFile={handleChangeFile}
            fileName={filename}
            setFileName={setFilename}
            handleUploadFile={handleUploadFile}
            label={t('front.factor.authorization.index.upload_file.label')}
            acceptedFile={['.txt']}
          />
          <div />
        </div>
        {latestAuthorizationFile && (
          <AuthorizationsFileCard authorizationFile={latestAuthorizationFile} />
        )}
        <Table
          onRowClick={handleOnRowClick}
          className="mt-[26px]"
          isLoading={isLoading}
          totalCount={totalCount}
          data={outstandingInvoices || []}
          reactTableProps={props}
          columns={outstandingInvoiceColumns}
        />
      </div>
    </div>
  );
};

export default OutstandingInvoicesIndex;
